/**
 * windowId — a short id that is unique per BROWSER WINDOW/TAB, not per device.
 *
 * WHY (Walter 2026-09-04). The content trail proved his paragraph was cut and
 * then restored eight seconds later, and showed a sentence ping-ponging
 * between two spellings every few seconds — two writers fighting over one
 * list. But it couldn't say WHO: he runs the installed PWA and a regular
 * Chrome window side by side, and because a PWA installed from Chrome shares
 * the profile, both windows share one origin, one localStorage, and therefore
 * one `clientId`. Every entry looked like it came from the same place.
 *
 * `sessionStorage` is the one web storage that is per-tab: each window gets
 * its own copy, and it survives reloads within that window. So this
 * distinguishes the PWA window from the Chrome window on the same machine,
 * which `clientId` structurally cannot.
 *
 * Deliberately NOT a replacement for clientId. That identifies the DEVICE and
 * drives real sync logic (`lastWriteByClientId`, the edit lock). This is a
 * diagnostic label only — nothing in the sync path should branch on it.
 */

const KEY = 'lists_window_id';

let cached = null;

export function getWindowId() {
  if (cached) return cached;
  try {
    let id = sessionStorage.getItem(KEY);
    if (!id) {
      id = Math.random().toString(36).slice(2, 7);
      sessionStorage.setItem(KEY, id);
    }
    cached = id;
    return id;
  } catch {
    // Private mode / storage blocked — a per-load id still separates two
    // windows for as long as neither reloads, which is enough to read a trail.
    cached = cached || Math.random().toString(36).slice(2, 7);
    return cached;
  }
}

/**
 * A hint at which SURFACE this window is, so a trail line reads
 * "w=a3f9/pwa" rather than just an opaque id. Standalone display-mode means
 * the installed app; everything else is a browser tab.
 */
export function getWindowKind() {
  try {
    if (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) return 'pwa';
    if (window.navigator && window.navigator.standalone) return 'pwa';
  } catch { /* ignore */ }
  return 'tab';
}

export function getWindowLabel() {
  return `${getWindowId()}/${getWindowKind()}`;
}
